import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import BackArrowIcon from 'assets/images/iPrise/BackArrow.svg';
import { saveAs } from 'file-saver';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchList } from 'services/common/react-query-common-service';
import { ReplaceData } from 'services/CommonServices';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT, SALES_ENDPOINT } from 'utils/Constant';

import CandidateMatchScoresScreen from '../../candidate-match-scores';
import { JobAssignStatus, TeamUserRoles } from '../../enum/TeamUserRoles';
import ShortlistModal from './ShortlistModal';

interface CandidateHeaderProps {
  id: string;
  candidateInfo: any;
  attachments: any[];
  setCandidateInfo: React.Dispatch<React.SetStateAction<any>>;
}

const CandidateHeader: React.FC<CandidateHeaderProps> = observer(
  ({ candidateInfo, attachments, setCandidateInfo, id }) => {
    const { userInfo: userDetail }: any = useAppSelector(
      (store: any) => store.userLogin,
    );
    const { viewJobServiceStore } = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const jobInfo = viewJobServiceStore.jobInfo;
    const [shortlistModalOpen, setShortlistModalOpen] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [openScoreModal, setOpenScoreModal] = useState(false);

    // Fetch overview file IDs directly inside CandidateHeader
    const { data: overviewResp, isLoading: isOverviewLoading } =
      useFetchList<any>(
        ['candidate-overview-ids', id],
        `jobs/${MASTER_ENDPOINT.Candidates}/overview-file/${String(id)}`,
        {
          enabled: !!id,
          refetchOnMount: 'always',
        },
      );

    const getOverviewData = React.useMemo(
      () => ({
        withGap: overviewResp?.jobOverviewFileId,
        withoutGap: overviewResp?.jobOverviewWithoutGapsFileId,
      }),
      [overviewResp],
    );

    const candidateName =
      candidateInfo?.candidate?.firstName && candidateInfo?.candidate?.lastName
        ? `${String(candidateInfo.candidate.firstName)} ${String(
            candidateInfo.candidate.lastName,
          )}`
        : candidateInfo?.name;
    const availability = (() => {
      const bs = candidateInfo?.candidate?.benchStatus;
      if (!bs) return '';
      return typeof bs === 'object' ? bs?.name ?? '' : String(bs);
    })();
    const availabilityDate = candidateInfo?.candidate?.benchAvailability || '';
    const email = candidateInfo?.candidate?.email || '';
    const phone =
      candidateInfo?.candidate?.phone ||
      candidateInfo?.candidate?.mobileNumber ||
      '';
    const candidateLocation =
      candidateInfo?.candidate?.city && candidateInfo?.candidate?.state
        ? `${String(candidateInfo.candidate.city)}, ${String(
            candidateInfo.candidate.state,
          )}`
        : candidateInfo?.candidate?.location || '';
    const matchPercentage =
      candidateInfo?.matchPercentage || candidateInfo?.matchScore || '';

    const visaStatus =
      candidateInfo?.candidate?.workStatus?.name ||
      candidateInfo?.candidate?.workAuth ||
      candidateInfo?.candidate?.visaStatus ||
      '';

    const payRate = ((): string => {
      if (jobInfo?.payRangeFrom && jobInfo?.payRangeTo) {
        return `$${String(jobInfo.payRangeFrom)} - $${String(
          jobInfo.payRangeTo,
        )}`;
      } else if (jobInfo?.payRangeFrom) {
        return `$${String(jobInfo.payRangeFrom)}`;
      }
      return '';
    })();
    const rawSource = candidateInfo?.source || jobInfo?.jobSource || '';
    const source =
      typeof rawSource === 'object'
        ? rawSource?.name ?? ''
        : String(rawSource || '');

    const onOverviewDownload = React.useCallback(() => {
      if (getOverviewData?.withGap) {
        const url = `${String(process.env.REACT_APP_APIURL)}${
          MASTER_ENDPOINT.Files
        }/${String(getOverviewData?.withGap)}`;
        const fileName = `${String(
          candidateInfo?.candidate?.firstName
            ? candidateInfo?.candidate?.firstName
            : '-',
        )}_${String(
          candidateInfo?.candidate?.lastName
            ? candidateInfo?.candidate?.lastName
            : '',
        )}_Overview`;
        saveAs(url, fileName);
      }
    }, [getOverviewData, candidateInfo]);
    return (
      <Box className="candidate-header">
        <Box className="top-row">
          <Box className="name-area">
            <IconButton className="back-button" onClick={() => navigate(-1)}>
              <img src={BackArrowIcon} alt="Back" />
            </IconButton>

            <Box className="name-info">
              <Box className="title-row">
                <Typography variant="h1">{candidateName}</Typography>
                {availability && (
                  <Box className="status-badge">
                    <Box className="dot" />
                    {availability}
                  </Box>
                )}
                {availabilityDate && (
                  <>
                    <Typography className="dot-separator">•</Typography>
                    <Typography className="availability-info">
                      {availabilityDate}
                    </Typography>
                  </>
                )}
              </Box>

              <Box className="contact-row-candidate">
                <span>{email}</span>
                <span className="dot-separator">•</span>
                <span>{phone}</span>
                <span className="dot-separator">•</span>
                <span>{candidateLocation}</span>
              </Box>
            </Box>
          </Box>

          <Box className="actions-area">
            <IconButton className="download-btn" onClick={onOverviewDownload}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
            {!candidateInfo?.isShortlisted &&
              userDetail?.teamUserRole === TeamUserRoles.RECRUITER &&
              jobInfo?.jobAssignStatus ===
                JobAssignStatus.RECRUITER_ACCEPTED && (
                <Button
                  variant="outlined"
                  className="shortlist-btn"
                  disabled={isSubmitting || isOverviewLoading}
                  onClick={() => setShortlistModalOpen(true)}>
                  {isOverviewLoading
                    ? 'Fetching...'
                    : isSubmitting
                    ? 'Shortlisting...'
                    : 'Shortlist Candidate'}
                </Button>
              )}
          </Box>
        </Box>

        <ShortlistModal
          open={shortlistModalOpen}
          onClose={() => setShortlistModalOpen(false)}
          attachments={attachments}
          isSubmitting={isSubmitting}
          onProceed={async (selected) => {
            if (isSubmitting) return;
            setIsSubmitting(true);

            const shortlistDocs = [
              ...(selected.includes('Resume')
                ? [
                    {
                      attachmentId: null,
                      documentType: 'resume',
                      fileId:
                        candidateInfo?.resumeId ||
                        candidateInfo?.candidate?.resumeId,
                      isSelected: true,
                    },
                  ]
                : []),
              ...(selected.includes('Candidate Overview Summary')
                ? [
                    {
                      attachmentId: null,
                      documentType: 'overviewSummary',
                      fileId: getOverviewData?.withoutGap,
                      isSelected: true,
                    },
                  ]
                : []),
              ...attachments
                .filter((att) =>
                  selected.includes(String(att.id || att.fileId)),
                )
                .map((att) => ({
                  attachmentId: att.id,
                  documentType: 'attachment',
                  fileId: att.fileId,
                  isSelected: true,
                })),
            ];

            const payload = {
              candidateId: candidateInfo?.candidate?.id,
              docs: shortlistDocs,
              jobMatchCandidateId: candidateInfo?.id,
              sourceQueue: 'jobQueue',
            };

            try {
              await ReplaceData(
                jobInfo?.id || '',
                payload,
                SALES_ENDPOINT.SHORTLIST,
                true,
              );

              // Update local state
              setCandidateInfo((prev: any) => ({
                ...prev,
                isShortlisted: true,
              }));

              // Update navigation state param
              navigate(location.pathname, {
                replace: true,
                state: {
                  ...location.state,
                  Params: {
                    ...location.state?.Params,
                    candidateInfo: {
                      ...candidateInfo,
                      isShortlisted: true,
                    },
                  },
                },
              });

              setShortlistModalOpen(false);
            } catch (error) {
              console.error('Error shortlisting candidate:', error);
            } finally {
              setIsSubmitting(false);
            }
          }}
        />

        <Box className="badges-row">
          {matchPercentage && (
            <Box
              className="badge match-score"
              onClick={() => setOpenScoreModal(true)}
              sx={{
                '&:hover': { opacity: 0.8 },
                cursor: 'pointer',
              }}>
              {matchPercentage}%
            </Box>
          )}
          {visaStatus && <Box className="badge visa-status">{visaStatus}</Box>}
          {payRate && (
            <Box className="badge pay-rate">Pay Rate • {payRate}</Box>
          )}
          {source && <Box className="badge source">{source}</Box>}
        </Box>

        <Dialog
          open={openScoreModal}
          onClose={() => setOpenScoreModal(false)}
          fullScreen>
          <CandidateMatchScoresScreen
            isModal={true}
            onClose={() => setOpenScoreModal(false)}
            candidates={candidateInfo ? [candidateInfo] : []}
            expFrom={jobInfo?.expFrom}
            expTo={jobInfo?.expTo}
            goal={jobInfo?.goal}
            jobTitle={jobInfo?.jobTitle}
            loading={false}
            selectedCandidateId={candidateInfo?.id}
          />
        </Dialog>
      </Box>
    );
  },
);

export default CandidateHeader;
